import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { PageIndices, TabIndices } from '../common/i-doc/i-doc.registry.ts';
import { PageHint } from '../components/common/PageHint';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Button,
  Chip,
  IconButton,
} from '@mui/material';
import { AdaptiveTabs } from '../components/common/AdaptiveTabs';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { getMeasurementHistory } from '../services/metrics.service';
import { getCalculatedMetrics } from '../services/calculatedMetrics.service';
import { LatestMeasurement, CalculatedMetricWithExplanation } from '../types/metrics';
import { AddMeasurementDialog } from '../components/metrics/AddMeasurementDialog';
import { MeasurementHistory } from '../components/metrics/MeasurementHistory';
import { MeasurementCharts } from '../components/metrics/MeasurementCharts';
import { MetricInfoModal } from '../components/CalculatedMetrics/MetricInfoModal';

interface ProcessedMeasurement {
  latest: LatestMeasurement;
  previous: LatestMeasurement | null;
  difference: number | null;
}

const METRICS_TABS = [
  { value: 'latest',   label: 'Останні вимірювання' },
  { value: 'history',  label: 'Історія' },
  { value: 'charts',   label: 'Графіки' },
  { value: 'metrics',  label: 'Показники' },
];

type MetricsTab = 'latest' | 'history' | 'charts' | 'metrics';

const MetricsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as MetricsTab | null;
  const currentTab: MetricsTab = METRICS_TABS.some(t => t.value === tabParam) ? tabParam! : 'latest';

  const [processedData, setProcessedData] = useState<ProcessedMeasurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [dataUpdateKey, setDataUpdateKey] = useState(0);

  const [calculatedMetrics, setCalculatedMetrics] = useState<CalculatedMetricWithExplanation[]>([]);
  const [calculatedMetricsLoading, setCalculatedMetricsLoading] = useState(false);
  const [calculatedMetricsError, setCalculatedMetricsError] = useState<string | null>(null);

  const [isMetricInfoModalOpen, setIsMetricInfoModalOpen] = useState(false);
  const [selectedMetricForModal, setSelectedMetricForModal] = useState<CalculatedMetricWithExplanation | null>(null);

  const fetchAndProcessData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const history = await getMeasurementHistory({ limit: 1000 });

      if (!Array.isArray(history)) {
        setProcessedData([]);
        return;
      }

      const grouped = history.reduce<Record<string, LatestMeasurement[]>>((acc, item) => {
        const typeId = item.measurementType.id;
        if (!acc[typeId]) acc[typeId] = [];
        acc[typeId].push(item);
        return acc;
      }, {});

      const processed = Object.values(grouped).map(group => {
        group.sort((a, b) => new Date(b.measuredAt).getTime() - new Date(a.measuredAt).getTime());
        const latest = group[0];
        const previous = group[1] || null;
        const difference = latest && previous ? latest.value - previous.value : null;
        return { latest, previous, difference };
      });

      setProcessedData(processed);
    } catch (err) {
      setError('Не вдалося завантажити вимірювання.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCalculatedMetricsData = useCallback(async () => {
    if (currentTab !== 'metrics') return;
    setCalculatedMetricsLoading(true);
    setCalculatedMetricsError(null);
    try {
      const data = await getCalculatedMetrics();
      setCalculatedMetrics(data);
    } catch (err) {
      setCalculatedMetricsError('Не вдалося завантажити розрахункові показники.');
      console.error(err);
    } finally {
      setCalculatedMetricsLoading(false);
    }
  }, [currentTab]);

  useEffect(() => {
    fetchAndProcessData();
  }, [fetchAndProcessData, dataUpdateKey]);

  useEffect(() => {
    fetchCalculatedMetricsData();
  }, [fetchCalculatedMetricsData]);

  const handleDataChange = () => setDataUpdateKey(prev => prev + 1);
  const handleAddSuccess = () => {
    setIsAddDialogOpen(false);
    handleDataChange();
  };

  const handleOpenMetricInfoModal = (metric: CalculatedMetricWithExplanation) => {
    setSelectedMetricForModal(metric);
    setIsMetricInfoModalOpen(true);
  };

  const handleCloseMetricInfoModal = () => {
    setIsMetricInfoModalOpen(false);
    setSelectedMetricForModal(null);
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const renderComparison = (item: ProcessedMeasurement) => {
    if (item.difference === null || item.difference === 0) return null;

    const isWeight = item.latest.measurementType.name.toLowerCase().includes('вага');
    const isGood = isWeight ? item.difference < 0 : item.difference > 0;
    const color = isWeight ? (isGood ? 'success' : 'error') : 'default';
    const icon = item.difference > 0 ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;

    return (
      <Chip
        icon={icon}
        label={`${item.difference.toFixed(1)} ${item.latest.unit.symbol}`}
        color={color}
        size="small"
        sx={{ mt: 1 }}
      />
    );
  };

  const renderLatestMeasurements = () => {
    if (loading) return <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (processedData.length === 0) return (
      <Box textAlign="center" sx={{ mt: 4 }}>
        <Typography>Наразі немає жодних вимірювань.</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => setIsAddDialogOpen(true)}>Додати перше вимірювання</Button>
      </Box>
    );

    return (
      <Grid container spacing={3}>
        {processedData.map(({ latest, ...rest }) => (
          <Grid item xs={12} sm={6} md={4} key={latest.id}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="div">{latest.measurementType.name}</Typography>
                <Box display="flex" alignItems="baseline" gap={1} sx={{ my: 1 }}>
                  <Typography variant="h4" component="p">{latest.value}</Typography>
                  <Typography variant="h6" color="text.secondary">{latest.unit.symbol}</Typography>
                </Box>
                {renderComparison({ latest, ...rest })}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Останнє оновлення: {formatDate(latest.measuredAt)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderCalculatedMetrics = () => {
    if (calculatedMetricsLoading) return <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
    if (calculatedMetricsError) return <Alert severity="error">{calculatedMetricsError}</Alert>;
    if (calculatedMetrics.length === 0) return (
      <Box textAlign="center" sx={{ mt: 4 }}>
        <Typography>Наразі немає розрахункових показників для цього користувача.</Typography>
      </Box>
    );

    return (
      <Grid container spacing={3}>
        {calculatedMetrics.map((metric) => (
          <Grid item xs={12} sm={6} md={4} key={metric.name}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" component="div">
                    {metric.caption ? `${metric.caption} (${metric.name})` : metric.name}
                  </Typography>
                  <IconButton size="small" onClick={() => handleOpenMetricInfoModal(metric)}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
                {metric.status === 'calculated' && metric.value !== undefined && (
                  <Box display="flex" alignItems="baseline" gap={0.5} sx={{ my: 0.5 }}>
                    <Typography variant="h5" component="p" sx={{ fontWeight: 'bold' }}>
                      {String(metric.value)}
                    </Typography>
                    {metric.unit && <Typography variant="body1" color="text.secondary">
                      {metric.unit}
                    </Typography>}
                  </Box>
                )}
                {metric.status === 'user_not_found' && (
                  <Alert severity="info" sx={{ mt: 1, py: 0, px: 1 }}>
                    <Typography variant="body2">{metric.message || 'Користувача не знайдено для розрахунку.'}</Typography>
                  </Alert>
                )}
                {metric.status === 'insufficient_data' && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0, px: 1 }}>
                    <Typography variant="body2">
                      Недостатньо даних для розрахунку.
                      {metric.missingParams && metric.missingParams.length > 0 && (
                        <> Відсутні параметри: {metric.missingParams.join(', ')}.</>
                      )}
                    </Typography>
                  </Alert>
                )}
                {metric.error && (
                  <Alert severity="error" sx={{ mt: 1, py: 0, px: 1 }}>
                    <Typography variant="body2">{metric.error}</Typography>
                  </Alert>
                )}
              </CardContent>
              {metric.category && (
                <Box sx={{ mt: 'auto', p: 2, pt: 0 }}>
                  <Chip label={metric.category} size="small" />
                </Box>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <DashboardLayout data-i-doc={PageIndices.METRICS}>
      <PageHint iDoc={PageIndices.METRICS} />
      <AdaptiveTabs tabs={METRICS_TABS} searchParamKey="tab" defaultValue="latest" />

      <Box sx={{ pb: 10 }}>
        {currentTab === 'latest'  && <Box sx={{ pt: 3 }} data-i-doc={TabIndices.METRICS_LATEST}><PageHint iDoc={TabIndices.METRICS_LATEST} />{renderLatestMeasurements()}</Box>}
        {currentTab === 'history' && <Box sx={{ pt: 3 }} data-i-doc={TabIndices.METRICS_HISTORY}><PageHint iDoc={TabIndices.METRICS_HISTORY} /><MeasurementHistory key={dataUpdateKey} onDataChange={handleDataChange} /></Box>}
        {currentTab === 'charts'  && <Box sx={{ pt: 3 }} data-i-doc={TabIndices.METRICS_CHARTS}><PageHint iDoc={TabIndices.METRICS_CHARTS} /><MeasurementCharts key={dataUpdateKey} /></Box>}
        {currentTab === 'metrics' && <Box sx={{ pt: 3 }} data-i-doc={TabIndices.METRICS_METRICS}><PageHint iDoc={TabIndices.METRICS_METRICS} />{renderCalculatedMetrics()}</Box>}
      </Box>

      <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 'fab' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsAddDialogOpen(true)} size="large">
          Додати вимірювання
        </Button>
      </Box>

      <AddMeasurementDialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} onSuccess={handleAddSuccess} />

      {selectedMetricForModal && (
        <MetricInfoModal
          open={isMetricInfoModalOpen}
          onClose={handleCloseMetricInfoModal}
          metricName={selectedMetricForModal.name}
        />
      )}
    </DashboardLayout>
  );
};

export default MetricsPage;
